import React, { useMemo } from 'react'
import { Separator } from '@components/ui/separator'
import { TooltipProvider } from '@components/ui/tooltip'
import { cn } from '@/utils'
import { useUnitOfMeasure } from '@/contexts/editor-settings-context'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import { getUnitLabel } from '@/lib/units/unit-conversion'
import {
  canBringSelectionForward,
  canSendSelectionBackward,
  liveLayerOrder,
} from '@/lib/layout/layer-order'
import { AlignButtons } from './components/align-buttons'
import { ArrangeButtons } from './components/arrange-buttons'
import { AutoSpacingToggle } from './components/auto-spacing-toggle'
import { LayoutPanelTextWrap } from './components/layout-panel-text-wrap'
import { MirrorButtons } from './components/mirror-buttons'
import { SectionHeading } from './components/section-heading'
import { SpaceBetweenButtons } from './components/space-between-buttons'
import { SpacingAmountControl } from './components/spacing-amount-control'
import { handleAlign } from './handlers/align-selection'
import { handleArrange } from './handlers/arrange-selection'
import { handleMirrorCopy } from './handlers/mirror-copy'
import { handleMirrorMove } from './handlers/mirror-move'
import { readLiveSelection } from './helpers/live-selection'
import { resolveMirrorAvailability } from './helpers/mirror-availability'
import { useSpaceDistribution } from './hooks/use-space-distribution'
import { useSpacingValue } from './hooks/use-spacing-value'
import type { LayoutPanelProps } from './types/layout-panel-props'

export type { LayoutPanelProps } from './types/layout-panel-props'

export const LayoutPanel: React.FC<LayoutPanelProps> = ({
  className,
  shapeManagerRef,
  onBatchShapesCommitted,
  onLayerReorder,
  onUnifiedLayerArrange,
  interactionLocked = false,
  unifiedLayerIds = [],
  mirrorSpineAxisX,
  onMirrorMoveHoverChange,
  textChains = [],
  onTextFramesLinked,
}) => {
  const { activeObjects } = useActiveObject()
  const unitOfMeasure = useUnitOfMeasure()
  const unitLabel = getUnitLabel(unitOfMeasure)
  const spacingUnitSuffix = unitOfMeasure === 'Picas' ? '' : unitLabel

  // Live read of the manager's selection (see `readLiveSelection`) drives Align, Space Between
  // and Mirror availability alike, instead of the activeObjects context, which can go stale.
  const selection = readLiveSelection(shapeManagerRef?.current)
  const selectedCount = selection.selectedShapes.length
  const canAlignMulti =
    !interactionLocked &&
    selectedCount >= 2 &&
    !!shapeManagerRef?.current &&
    !!onBatchShapesCommitted
  // Space Between needs exactly what multi-select Align needs (2+ visible selected shapes).
  const canSpaceSelection = canAlignMulti
  const canAutoDistribute = canSpaceSelection && selectedCount >= 3

  const mirrorAvailability = resolveMirrorAvailability(selection, {
    interactionLocked,
    mirrorSpineAxisX,
    hasShapeManager: !!shapeManagerRef?.current,
    hasBatchCommit: !!onBatchShapesCommitted,
  })

  // Effect order matters: the auto-distribute effect (useSpaceDistribution) runs before the
  // spacing-input sync effect (useSpacingValue).
  const distribution = useSpaceDistribution({
    shapeManagerRef,
    onBatchShapesCommitted,
    selectedCount,
    canSpaceSelection,
    canAutoDistribute,
  })
  const spacing = useSpacingValue({
    unitOfMeasure,
    spacingUnitSuffix,
    autoDistribute: distribution.autoDistribute,
    canSpaceSelection,
  })

  // Arrange availability is based on the unified (shapes + text) z-order, not per-type
  // sibling counts — a lone shape can still have text above/below it, and a page with a
  // single reorderable object must disable all four actions.
  // Deleted objects keep their id in the order, so only ids still on the canvas count
  // (WMPCM-12998), the same as the context menu.
  const arrangeSelectedIds = useMemo(() => activeObjects.map((o) => o.id), [activeObjects])
  const arrangeLayerIds = liveLayerOrder(unifiedLayerIds, shapeManagerRef?.current?.getShapes())
  const canArrangeBringUp =
    !interactionLocked && canBringSelectionForward(arrangeSelectedIds, arrangeLayerIds)
  const canArrangeSendDown =
    !interactionLocked && canSendSelectionBackward(arrangeSelectedIds, arrangeLayerIds)

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          'space-y-4',
          className,
          interactionLocked && 'pointer-events-none select-none opacity-60'
        )}
        aria-disabled={interactionLocked || undefined}
      >
        <SectionHeading>Alignment and Position</SectionHeading>

        <AlignButtons
          disabled={!canAlignMulti}
          onAlign={(key) => handleAlign(shapeManagerRef?.current, onBatchShapesCommitted, key)}
        />

        <ArrangeButtons
          canBringUp={canArrangeBringUp}
          canSendDown={canArrangeSendDown}
          onArrange={(op) =>
            handleArrange(op, {
              mgr: shapeManagerRef?.current,
              selectedIds: activeObjects.map((o) => o.id),
              onLayerReorder,
              onUnifiedLayerArrange,
            })
          }
        />

        <Separator />

        <SectionHeading>Spacing</SectionHeading>

        <SpaceBetweenButtons
          disabled={!canSpaceSelection}
          onDirectionClick={(direction) =>
            distribution.handleSpaceDirectionClick(direction, spacing.spaceBetween[0])
          }
        />

        <AutoSpacingToggle
          checked={distribution.autoDistribute}
          canAutoDistribute={canAutoDistribute}
          selectedCount={selectedCount}
          onCheckedChange={distribution.handleAutoDistributeChange}
        />

        {!distribution.autoDistribute && (
          <SpacingAmountControl
            disabled={!canSpaceSelection}
            spacingOptions={spacing.spacingOptions}
            selectedSpacing={spacing.selectedSpacing}
            spaceBetween={spacing.spaceBetween}
            spacingInputValue={spacing.spacingInputValue}
            spacingUnitSuffix={spacingUnitSuffix}
            onValueChange={(value) => spacing.handleSpacingValueChange(value)}
            onInputChange={spacing.setSpacingInputValue}
            onInputCommit={spacing.commitSpacingInputValue}
            onInputKeyDown={spacing.handleSpacingInputKeyDown}
          />
        )}

        <LayoutPanelTextWrap
          shapeManagerRef={shapeManagerRef}
          onBatchShapesCommitted={onBatchShapesCommitted}
          interactionLocked={interactionLocked}
        />

        <Separator />

        <SectionHeading>Transformation</SectionHeading>

        <MirrorButtons
          availability={mirrorAvailability}
          onMirrorMove={() =>
            handleMirrorMove({
              mgr: shapeManagerRef?.current,
              mirrorSpineAxisX,
              onBatchShapesCommitted,
              onMirrorMoveHoverChange,
            })
          }
          onMirrorCopy={() =>
            handleMirrorCopy({
              mgr: shapeManagerRef?.current,
              mirrorSpineAxisX,
              textChains,
              onTextFramesLinked,
              onMirrorMoveHoverChange,
            })
          }
          onHoverChange={onMirrorMoveHoverChange}
        />
      </div>
    </TooltipProvider>
  )
}
